import { is } from 'immutable';
import { createSelector, createSelectorCreator, createStructuredSelector, defaultMemoize, Selector } from 'reselect';

import { NotebookTypes } from '@mode/notebooks/contract';
import { AppState } from '@mode/legacy/types-app-state';
import {
  NormalizedNotebook,
  NormalizedNotebookCell,
  NormalizedNotebookCellArray,
  NormalizedNotebookCellExRes,
  NormalizedNotebookCellRun,
  NormalizedNotebookCellRunArray,
  NormalizedNotebookCellRunResult,
  NormalizedNotebookCellRunResultArray,
  NormalizedNotebooksArray,
  NotebookCellState,
  NotebookEmbedVizResult,
  NotebookRunState,
  NotebookState,
  NotebookVizRender,
  NotebookVizState,
} from '@mode/legacy/types-app-state';
import { NotebookHelpers } from '~/lib/notebooks';
import { isPresent } from '@mode/shared/util-js';

export enum AceLanguage {
  Python = 'python',
  R = 'r',
  Markdown = 'markdown',
}

function immutableEqualityCheck(a: any, b: any): boolean {
  return is(a, b);
}

function resultEqualityCheck(a: any, b: any): boolean {
  if (immutableEqualityCheck(a, b)) {
    return true;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    const xArr = a as NormalizedNotebookCellRunResult[];
    const yArr = b as NormalizedNotebookCellRunResult[];

    if (xArr.length === yArr.length) {
      return xArr.every(
        (x, ii) =>
          x.content === yArr[ii].content &&
          x.content_type === yArr[ii].content_type &&
          x.result_type === yArr[ii].result_type &&
          x.token === yArr[ii].token
      );
    } else {
      return false;
    }
  }

  if (a != null && b != null) {
    const x = a as NormalizedNotebookCellRunResult;
    const y = a as NormalizedNotebookCellRunResult;
    return (
      x.content === y.content &&
      x.content_type === y.content_type &&
      x.result_type === y.result_type &&
      x.token === y.token
    );
  }

  return false;
}

const createImmutableCheckSelector = createSelectorCreator(defaultMemoize, immutableEqualityCheck);
const createResultCheckSelector = createSelectorCreator(defaultMemoize, resultEqualityCheck);

function getNormalizedNotebook(notebooks: NormalizedNotebooksArray, token: string): NormalizedNotebook {
  return {
    token,
    pusherChannelName: notebooks.pusherChannelNames.get(token),
    initialized: notebooks.initialized.get(token),
    resource: notebooks.resources.get(token),
    session: notebooks.sessions.get(token),
    cellTokens:
      (notebooks.cellTokensByNotebook.get(token) && notebooks.cellTokensByNotebook.get(token).toArray()) || [],
  };
}

function getNormalizedCell(cells: NormalizedNotebookCellArray, token: string): NormalizedNotebookCell {
  return {
    token,
    content: cells.contents.get(token),
    content_type: cells.contentTypes.get(token),
    created_at: cells.createdAt.get(token),
    frozen: cells.frozen.get(token),
    executing: cells.executing.get(token),
    lastRunToken: cells.lastRunTokens.get(token),
    missingRun: cells.missingRuns.has(token),
    resource: cells.resources.get(token),
    folded: cells.folded.get(token),
  };
}

function getNormalizedCellRun(runs: NormalizedNotebookCellRunArray, token: string): NormalizedNotebookCellRun {
  return {
    token,
    execution_count: runs.executionCounts.get(token),
    resource: runs.resources.get(token),
    resultTokens: runs.resultTokens.get(token).toArray(),
    missingResult: runs.missingResults.has(token),
  };
}

function getNormalizedCellRunResult(
  results: NormalizedNotebookCellRunResultArray,
  token: string
): NormalizedNotebookCellRunResult {
  return {
    token,
    result_type: results.resultTypes.get(token),
    content_type: results.contentTypes.get(token),
    date: results.dates.get(token),
    content: results.contents.get(token),
    resource: results.resources.get(token),
  };
}

function notebookPath(state: AppState) {
  return state.client.notebooks;
}

function cellPath(state: AppState) {
  return notebookPath(state).cells;
}

function runPath(state: AppState) {
  return notebookPath(state).cellRuns;
}

function resultsPath(state: AppState) {
  return notebookPath(state).cellRunResults;
}

function vizPath(state: AppState) {
  return state.client.selectedReport.notebookViz;
}

export namespace NotebookSelectors {
  export const notebookTokensArray = (state: AppState) => notebookPath(state).books.tokens;
  export const notebookCellTokensArray = (state: AppState) => notebookPath(state).books.cellTokensByNotebook;
  export const notebookSessionsArray = (state: AppState) => notebookPath(state).books.sessions;
  export const notebookStatesArray = (state: AppState) => notebookPath(state).books.states;
  export const notebookTypesArray = (state: AppState) => notebookPath(state).books.notebookTypes;
  export const notebookAwaitingRunArray = (state: AppState) => notebookPath(state).books.awaitingInitialRun;
  export const notebookResourcesArray = (state: AppState) => notebookPath(state).books.resources;

  export const cellContentsArray = (state: AppState) => cellPath(state).contents;
  export const cellContentTypesArray = (state: AppState) => cellPath(state).contentTypes;
  export const cellCreatedAtArray = (state: AppState) => cellPath(state).createdAt;
  export const cellExecutingArray = (state: AppState) => cellPath(state).executing;
  export const cellFoldedArray = (state: AppState) => cellPath(state).folded;
  export const cellFrozenArray = (state: AppState) => cellPath(state).frozen;
  export const cellLastRunTokensArray = (state: AppState) => cellPath(state).lastRunTokens;
  export const cellMissingRunsArray = (state: AppState) => cellPath(state).missingRuns;
  export const cellResourcesArray = (state: AppState) => cellPath(state).resources;

  export const runsResultTokensArray = (state: AppState) => runPath(state).resultTokens;
  export const runsExecutionCountsArray = (state: AppState) => runPath(state).executionCounts;

  export const resultsTokensArray = (state: AppState) => resultsPath(state).tokens;

  export const vizTokensArray = (state: AppState) => vizPath(state).tokens;
  export const vizCellTokensArray = (state: AppState) => vizPath(state).cellTokens;
  export const vizCellRunTokensArray = (state: AppState) => vizPath(state).cellRunTokens;
  export const vizTitlesArray = (state: AppState) => vizPath(state).titles;
  export const vizResourcesArray = (state: AppState) => vizPath(state).resources;
  export const vizRunState = (state: AppState) => vizPath(state).runState;

  // Currently a report can only have one notebook
  export const selectedNotebookToken = (state: AppState) => notebookTokensArray(state).first<string>(null);

  export const selectedNotebookCellTokens = createSelector(
    selectedNotebookToken,
    notebookCellTokensArray,
    (token, cellTokens) => cellTokens.get(token)
  );

  export const selectedNotebookSession = createSelector(
    selectedNotebookToken,
    notebookSessionsArray,
    (notebookToken, sessions) => sessions.get(notebookToken)
  );

  export const selectedNotebookResource = createSelector(
    selectedNotebookToken,
    notebookResourcesArray,
    (notebookToken, resources) => resources.get(notebookToken)
  );

  export const selectedNotebookType = createSelector(
    selectedNotebookToken,
    notebookTypesArray,
    (notebookToken, types) => types.get(notebookToken)
  );

  export const selectedNotebookState = createSelector(
    selectedNotebookToken,
    notebookStatesArray,
    (notebookToken, states) => states.get(notebookToken)
  );

  export const selectedNotebookLabel = createSelector(selectedNotebookType, (type) => {
    if (!type) {
      return 'Add Notebook';
    } else {
      return type.includes('python') ? 'Python Notebook' : 'R Notebook';
    }
  });

  export const selectedNotebookIcon = createSelector(selectedNotebookType, (type) => {
    if (!type) {
      return 'icon-notebook-gray';
    } else {
      return type.includes('python') ? 'icon-notebook-py-gray' : 'icon-notebook-r-gray';
    }
  });

  export const selectedNotebookSvgIcon = createSelector(selectedNotebookType, (type) => {
    if (!type) {
      return 'sidebar-new-notebook';
    } else {
      return type.includes('python') ? 'sidebar-python-notebook' : 'sidebar-r-notebook';
    }
  });

  export const selectedNotebookCellsExecutionCount = createSelector(runsExecutionCountsArray, (executionCounts) =>
    executionCounts.count()
  );

  export const reportVisuals = createSelector(
    vizTokensArray,
    vizCellTokensArray,
    vizTitlesArray,
    vizResourcesArray,
    cellLastRunTokensArray,
    runsResultTokensArray,
    (state: AppState) => state.client.notebooks.cellRunResults,
    (vizTokens, vizCellTokens, vizTitles, vizResources, runTokens, runResultTokens, resultsArray) =>
      vizTokens
        .map((t) => {
          const cellToken = vizCellTokens.get(t);
          const runToken = runTokens.get(cellToken);
          const resultTokens = runResultTokens.get(runToken);

          if (resultTokens != null) {
            const results = resultTokens.map((rt) => getNormalizedCellRunResult(resultsArray, rt));

            return {
              token: t,
              title: vizTitles.get(t),
              vizResource: vizResources.get(t),
              results: results.toArray(),
            };
          }
        })
        .filter((viz) => viz != null)
        .toArray()
  );

  export const cellTokensForSelectedReport = createSelector(vizTokensArray, vizCellTokensArray, (tokens, cellTokens) =>
    tokens.map((t) => cellTokens.get(t))
  );

  export const isEditing = (state: AppState) => notebookPath(state).editor.isEditing;

  // Selected cell selectors
  export const selectedCellToken = (state: AppState) => state.client.notebooks.editor.selectedCellToken;

  export const editingCellToken = createSelector(
    selectedCellToken,
    isEditing,
    (cellToken, editing) => editing && cellToken
  );

  export const selectedCellExecuting = createSelector(
    selectedCellToken,
    cellExecutingArray,
    (cellToken, executing) => executing.get(cellToken) || false
  );

  export const selectedCellContentType = createSelector(
    selectedCellToken,
    cellContentTypesArray,
    (cellToken, contentTypes) => contentTypes.get(cellToken)
  );

  export const selectedCellContent = createSelector(selectedCellToken, cellContentsArray, (cellToken, contents) =>
    contents.get(cellToken)
  );

  export const selectedCellIsInLayout = createSelector(
    selectedCellToken,
    cellTokensForSelectedReport,
    (cellToken, reportCells) => reportCells.has(cellToken)
  );

  export const selectedCellFrozen = createSelector(selectedCellToken, cellFrozenArray, (cellToken, frozen) =>
    frozen.get(cellToken)
  );

  export const selectedCellFolded = createSelector(selectedCellToken, cellFoldedArray, (cellToken, folded) =>
    folded.get(cellToken)
  );

  export const selectedCellHasResult = createSelector(
    selectedCellToken,
    selectedCellContentType,
    cellLastRunTokensArray,
    runsResultTokensArray,
    (cellToken, contentType, lastRunTokens, resultTokens) =>
      cellToken &&
      contentType !== NotebookTypes.ContentType.Markdown &&
      lastRunTokens.has(cellToken) &&
      resultTokens.has(lastRunTokens.get(cellToken)) &&
      resultTokens.get(lastRunTokens.get(cellToken)).size > 0
  );

  export const selectedNextCellToken = createSelector(
    selectedCellToken,
    selectedNotebookCellTokens,
    (cellToken, cells) => {
      if (cells) {
        const arr = cells.toIndexedSeq();
        const index = arr.indexOf(cellToken);
        if (index > -1) {
          return arr.get(index + 1);
        }
      }
    }
  );

  export const selectedPrevCellToken = createSelector(
    selectedCellToken,
    selectedNotebookCellTokens,
    (cellToken, cells) => {
      if (cells) {
        const arr = cells.toIndexedSeq();
        const index = arr.indexOf(cellToken);
        if (index > 0) {
          return arr.get(index - 1);
        }
      }
    }
  );

  // End Selected cell selectors

  //-- SELECTOR CREATORS
  export function createCellTokensInNotebookSelector(notebookToken: string) {
    return createSelector(notebookCellTokensArray, (tokensByNotebook) => tokensByNotebook.get(notebookToken));
  }

  export function createResultTokensByVizSelector(vizToken: string) {
    return createSelector(
      (state: AppState) => state.client.selectedReport.notebookViz.cellTokens.get(vizToken),
      (state: AppState) => state.client.selectedReport.notebookViz.cellRunTokens,
      (state: AppState) => state.client.notebooks.cellRuns.missingResults,
      (state: AppState) => state.client.notebooks.cellRuns.resultTokens,
      (cellToken, runTokens, missingResults, resultTokens) => {
        if (cellToken && runTokens.has(cellToken)) {
          // the notebookViz.cellTokens, notebooks.cells.lastRunTokens etc Maps are
          // populated one-by-one in a flurry of actions, but state subscribers are
          // called on every action, including ones midway through the action flow
          // where those Maps haven't been fully populated yet. So we need these
          // guards here to distinguish being midway through the action flow from
          // being done but the cell run results are missing.
          const runToken = runTokens.get(cellToken);
          if (missingResults.has(runToken)) {
            return NotebookVizState.Empty;
          }
          const tokens = resultTokens.get(runToken);
          if (tokens) {
            return tokens.filter((t) => t != null);
          }
        }
        return NotebookVizState.Loading;
      }
    );
  }

  export function createLastResultsByCellSelector(cellToken: string) {
    // Create a selector for pulling out the normalized results for the provided cell token
    const cellResultsSelector = createImmutableCheckSelector(
      (state: AppState) => runsResultTokensArray(state).get(cellLastRunTokensArray(state).get(cellToken)),
      (state: AppState) => state.client.notebooks.cellRunResults,
      (resultTokens, results) => {
        const mergedResults =
          (resultTokens &&
            results.tokens
              .intersect(resultTokens)
              .map((t) => getNormalizedCellRunResult(results, t))
              .toArray()) ||
          [];
        return mergedResults.filter((r) => r.content != null);
      }
    );

    // Return a memoized selector that will do a deep equality check to see if the results have changed
    return createResultCheckSelector(cellResultsSelector, (r) => r);
  }

  export function createMemoizedResultsByVizSelector(vizToken: string) {
    // Create a selector for pulling out the normalized results for the provided cell token
    return createImmutableCheckSelector(
      createResultTokensByVizSelector(vizToken),
      (state: AppState) => state.client.notebooks.cellRunResults,
      (resultTokens, results) => {
        if (resultTokens === NotebookVizState.Empty) {
          return NotebookVizState.Empty;
        }
        // cellRuns.resultTokens, cellRunResults.contentTypes, and
        // cellRunResults.resultTypes are all populated by
        // LoadCellRunResultsSuccess, whereas cellRunResults.contents isn't
        // populated until LoadCellRunResultContentSuccess (after a request
        // to S3), so it's the only one that needs a .isSubset() check
        if (resultTokens === NotebookVizState.Loading || !resultTokens.isSubset(results.contents.keySeq())) {
          return NotebookVizState.Loading;
        }

        return resultTokens.map((t) => getNormalizedCellRunResult(results, t)).toArray();
      }
    );
  }

  export function createVizRenderSelector(vizToken: string): (state: AppState) => NotebookVizRender {
    return createSelector(
      createMemoizedResultsByVizSelector(vizToken),
      (state: AppState) => state.client.selectedReport.notebookViz.frozen,
      (state: AppState) => state.client.selectedReport.notebookViz.titles,
      (state: AppState) => state.client.selectedReport.notebookViz.runState,
      (state: AppState) => state.client.selectedReport.notebookViz.resources,
      (state: AppState) => state.client.selectedReport.reportRun,
      (results, frozenByViz, titlesByViz, runState, resourceByViz, reportRun) => {
        const res = resourceByViz.get(vizToken);
        if (res == null) {
          return null;
        }

        const vizLink = res.hasLink('report_run_pythonviz_web')
          ? res.links('report_run_pythonviz_web').$expand({ token: reportRun.token }, { excludeSSLPort: true })
          : '';
        const vizReportRunLink = res.hasLink('report_run_pythonviz_web')
          ? res.links('report_run_pythonviz_web').$expand({ token: reportRun.token }, { excludeSSLPort: true })
          : '';
        const vizName: 'pythonVisualization' = 'pythonVisualization';
        function viz(state: NotebookVizState, results: NotebookEmbedVizResult[]) {
          return {
            _name: vizName,
            id: 'python_' + res.token,
            token: res.token,
            title: titlesByViz.get(res.token),
            frozen: frozenByViz.has(res.token),
            state,
            vizLink,
            vizReportRunLink,
            results,
          };
        }

        if (runState === NotebookRunState.Failed) {
          return viz(NotebookVizState.Failed, []);
        }

        if (results === NotebookVizState.Loading || results === NotebookVizState.Empty) {
          return viz(results, []);
        }

        return viz(
          NotebookVizState.Succeeded,
          results.map((result) => ({
            token: res.token,
            content: result.content,
            type: result.content_type,
            result_type: result.result_type,
            date: result.date,
            content_type: result.content_type,
          }))
        );
      }
    );
  }

  /**
   * Returns everything needed to emit a preview update for a cell result
   */
  export function createVizByCellSelector(cellToken: string) {
    // TODO: add return type
    const vizForCell = createSelector(
      (state: AppState) => vizCellTokensArray(state).findKey((c) => cellToken === c),
      vizTitlesArray,
      vizResourcesArray,
      createLastResultsByCellSelector(cellToken),
      (vizToken, vizTitles, vizResources, results) => ({
        vizToken,
        title: vizTitles.get(vizToken),
        vizResource: vizResources.get(vizToken),
        results,
      })
    );

    return vizForCell;
  }

  export function createIsNotebookReadySelector(notebookToken: string): Selector<AppState, boolean> {
    return createSelector(notebookTokensArray, (tokens) => tokens.has(notebookToken));
  }

  export function createCellExecutionCountSelector(cellToken: string): Selector<AppState, number> {
    const lastRunToken = (state: AppState) => cellPath(state).lastRunTokens.get(cellToken);
    return createSelector(lastRunToken, runsExecutionCountsArray, (runToken, executionCounts) =>
      executionCounts.get(runToken)
    );
  }

  export function createNormalizedCellExResSelector(token: string): Selector<AppState, NormalizedNotebookCellExRes> {
    return createStructuredSelector({
      token: () => token, // Not sure if this is hacky or not
      content: (state: AppState) => cellContentsArray(state).get(token),
      content_type: (state: AppState) => cellContentTypesArray(state).get(token),
      created_at: (state: AppState) => cellCreatedAtArray(state).get(token),
      frozen: (state: AppState) => cellFrozenArray(state).get(token),
      executing: (state: AppState) => cellExecutingArray(state).get(token),
      lastRunToken: (state: AppState) => cellLastRunTokensArray(state).get(token),
      folded: (state: AppState) => cellFoldedArray(state).get(token),
      missingRun: (state: AppState) => cellMissingRunsArray(state).has(token),
    });
  }

  export function createIsCellInLayoutSelector(cellToken: string): Selector<AppState, boolean> {
    return createSelector(cellTokensForSelectedReport, (cellsInReport) => cellsInReport.contains(cellToken));
  }

  export function createIsCellEditingSelector(cellToken: string): Selector<AppState, boolean> {
    return createSelector(
      selectedCellToken,
      isEditing,
      (selectedToken, editing) => selectedToken === cellToken && editing
    );
  }

  export function createMarkdownViewModeSelector(cellToken: string): Selector<AppState, boolean> {
    return createSelector(
      createIsCellEditingSelector(cellToken),
      (state: AppState) => cellContentTypesArray(state).get(cellToken),
      (editing, contentType) => !editing && contentType === NotebookTypes.ContentType.Markdown
    );
  }

  export function createNotebookStateSelector(notebookToken: string): Selector<AppState, NotebookState> {
    return createSelector(
      (state: AppState) => notebookStatesArray(state).get(notebookToken),
      (state) => state
    );
  }

  export function createCanCellRunSelector(notebookToken: string): Selector<AppState, boolean> {
    return createSelector(
      createNotebookStateSelector(notebookToken),
      NotebookSelectors.selectedCellToken,
      selectedCellExecuting,
      selectedCellContentType,
      selectedCellContent, // a super minor optimization would be to make a selectedCellContentEmpty selector
      selectedCellFrozen,
      (state, cellToken, executing, contentType, content, isFrozen) =>
        // only when notebook is ready and cell isn't already running. Must also be nonempty (ignoring whitespace)
        state === NotebookState.Ready &&
        cellToken != null &&
        !executing &&
        !isFrozen &&
        content != null &&
        contentType !== NotebookTypes.ContentType.Markdown &&
        content.trim().length > 0
    );
  }

  // This is just for the ace editor theme
  export function createCellAceLangSelector(cellToken: string): Selector<AppState, AceLanguage> {
    return createSelector(selectedNotebookType, cellContentTypesArray, (notebookType, cellTypes) => {
      if (cellTypes.get(cellToken) === NotebookTypes.ContentType.Markdown) {
        return AceLanguage.Markdown;
      }

      switch (notebookType) {
        case NotebookTypes.NotebookType.R:
          return AceLanguage.R;
        default:
          return AceLanguage.Python;
      }
    });
  }

  export function createRunnableCellTokensSelector(notebookToken: string) {
    return createSelector(
      (state: AppState) => notebookCellTokensArray(state).get(notebookToken),
      cellFrozenArray,
      cellContentTypesArray,
      cellContentsArray,
      (tokens, frozen, contentTypes, contents) =>
        tokens.filterNot((t) => frozen.get(t) || contentTypes.get(t) === 'markdown' || !contents.get(t))
    );
  }

  export const createCellContentTypeSelector = (cellToken: string) => (state: AppState) =>
    state.client.notebooks.cells.contentTypes.get(cellToken);

  //-- PARAMETERIZED SELECTORS
  export const notebookTokenByNotebook = (state: AppState, notebookToken: string) =>
    notebookTokensArray(state).get(notebookToken);
  export const normalizedNotebookByToken = createSelector(
    (state: AppState, notebookToken: string) => notebookToken,
    (state: AppState) => state.client.notebooks.books,
    (token, books) => getNormalizedNotebook(books, token)
  );

  export const cellTokensByNotebook = (state: AppState, notebookToken: string) =>
    notebookCellTokensArray(state).get(notebookToken);

  /**
   * Non-frozen cell tokens for use when determining what cells to run when run notebook called
   */
  export const runnableCellTokensByNotebook = createSelector(
    cellTokensByNotebook,
    cellFrozenArray,
    cellContentTypesArray,
    cellContentsArray,
    (tokens, frozen, contentTypes, contents) =>
      tokens.filterNot((t) => frozen.get(t) || contentTypes.get(t) === 'markdown' || !contents.get(t))
  );
  export const normalizedCellsByNotebook = createSelector(
    cellTokensByNotebook,
    (state: AppState) => cellPath(state),
    (cellTokens, cells) => {
      if (cellTokens) {
        return cells.tokens
          .filter((t) => cellTokens.contains(t))
          .map((t) => getNormalizedCell(cells, t))
          .toArray();
      }
    }
  );
  export const cellsByNotebook = createSelector(
    normalizedCellsByNotebook,
    cellTokensForSelectedReport,
    (cells, cellsInReport) => {
      if (cells) {
        return cells.map<NotebookCellState>((cell) => ({
          ...cell,
          isInLayout: cellsInReport.contains(cell.token),
        }));
      }
    }
  );

  export const cellRunTokensByNotebook = createSelector(
    cellTokensByNotebook,
    cellLastRunTokensArray,
    (cellTokens, lastRunTokens) =>
      lastRunTokens
        .filter((runToken, cellToken) => cellTokens.has(cellToken))
        .valueSeq()
        .toSet()
  );

  export const cellRunResultTokensByNotebook = createSelector(
    cellRunTokensByNotebook,
    runsResultTokensArray,
    (cellTokens, resultTokens) =>
      resultTokens
        .filter((resultToken, cellToken) => cellTokens.has(cellToken))
        .valueSeq()
        .flatten()
        .toSet()
  );

  export const sessionByNotebook = (state: AppState, notebookToken: string) =>
    notebookSessionsArray(state).get(notebookToken);

  // Might be possible to use notebook idle event from pusher to make this simpler
  export const isRunningByNotebook = createSelector(cellTokensByNotebook, cellExecutingArray, (tokens, executing) =>
    executing.some((exec, token) => exec && tokens.has(token))
  );
  export const stateByNotebook = createSelector(
    isRunningByNotebook,
    (state: AppState, notebookToken: string) => notebookStatesArray(state).get(notebookToken),
    (isRunning, state) => (isRunning ? NotebookState.Running : state)
  );
  export const statusBarStateByNotebook = createSelector(stateByNotebook, (state) => {
    switch (state) {
      case NotebookState.Busy:
        return NotebookState.Error;
      case NotebookState.Session:
        return NotebookState.Loading;
      default:
        return state;
    }
  });
  export const stateMessageByNotebook = createSelector(
    stateByNotebook,
    sessionByNotebook,
    selectedNotebookResource,
    (state, session, resource) => {
      switch (state) {
        case NotebookState.Error:
          if (session) {
            switch (session.termination_reason) {
              case 'CRASHED':
                return 'Notebook has encountered an unexpected error';
              case 'EXPIRED':
                return 'Notebook session ended due to inactivity';
              case 'OOM':
                return 'Notebook ran out of memory';
              default:
                return 'Notebook session ended';
            }
          }
          break;
        case NotebookState.Busy:
          return 'Notebook session currently unavailable';
        case NotebookState.Loading:
          return 'Loading data frames';
        case NotebookState.Session:
          return 'Setting up notebook';
        case NotebookState.Running:
          return resource.warn ? '. Expect wait time due to increased Memory usage' : '';
        default:
          return '';
      }
    }
  );
  export const canRunByNotebook = createSelector(
    sessionByNotebook,
    stateByNotebook,
    (session, state) => session && session.active && state === NotebookState.Ready
  );

  export const canFreezeByCell = createSelector(
    (state: AppState, cellToken: string) => cellContentTypesArray(state).get(cellToken),
    (contentType) => contentType !== NotebookTypes.ContentType.Markdown
  );

  export const vizTokenByCell = (state: AppState, cellToken: string) =>
    vizCellTokensArray(state).findKey((t) => t === cellToken);

  export const isExportedByCell = createSelector(
    (state: AppState, cellToken: string) => cellResourcesArray(state).get(cellToken),
    (cellResource) => isPresent(cellResource.local_dataset_exported_at)
  );

  export const isPublishedDatasetCell = createSelector(
    (state: AppState, cellToken: string) => cellResourcesArray(state).get(cellToken),
    (cellResource) => isPresent(cellResource?.thoughtspot_published_at)
  );
}
